import { createMemo, createResource, createSignal, getOwner, onCleanup, DEV } from 'solid-js';
import { isServer } from 'solid-js/web';

var __require = /* @__PURE__ */ ((x) => typeof require !== "undefined" ? require : typeof Proxy !== "undefined" ? new Proxy(x, {
  get: (a, b) => (typeof require !== "undefined" ? require : a)[b]
}) : x)(function(x) {
  if (typeof require !== "undefined")
    return require.apply(this, arguments);
  throw Error('Dynamic require of "' + x + '" is not supported');
});
var fetchFallback = globalThis.fetch;
if (isServer && !fetchFallback) {
  try {
    const nodeFetch = __require("node-fetch");
    fetchFallback = nodeFetch;
  } catch (_e) {
    fetchFallback = () => {
      console.warn(
        '"\x1B[33m\u26A0\uFE0F package missing to run createFetch on the server.\n Please run:\x1B[0m\n\nnpm i node-fetch\n"'
      );
      return Promise.reject(new Error("fetch not available"));
    };
  }
}
var fetchRequest = (fetchFn) => (requestContext) => {
  requestContext.fetcher = (requestData) => (fetchFn || fetchFallback)?.(...requestData).then((response) => {
    requestContext.response = response;
    if (requestContext.responseHandler) {
      return requestContext.responseHandler(response);
    }
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      return response.json();
    } else if (contentType.includes("text/")) {
      return response.text();
    } else {
      return response.blob();
    }
  });
  requestContext.wrapResource();
};

// src/fetch.ts
var isOptions = (prop) => typeof prop === "object" && ["name", "initialValue", "fetch", "request"].some((key) => key in prop);
var fetcherArgsFromArgs = (args) => {
  const info = typeof args[0] === "function" ? args[0]() : args[0];
  if (!info) {
    return void 0;
  }
  const init = typeof args[1] === "function" ? args[1]() : isOptions(args[1]) || Array.isArray(args[1]) ? void 0 : args[1];
  return [info, init];
};
function createFetch(...args) {
  const options = [args[2], args[1]].find(isOptions) || {};
  const urlAccessor = options.disable ? () => void 0 : typeof args[0] === "function" || typeof args[1] === "function" ? createMemo(() => fetcherArgsFromArgs(args)) : ((fetcherArgs) => () => fetcherArgs)(fetcherArgsFromArgs(args));
  const modifiers = (() => {
    for (let l = args.length - 1; l >= 1; l--) {
      if (Array.isArray(args[l])) {
        return args[l];
      }
    }
    return [];
  })();
  modifiers.unshift(options.request || fetchRequest(options.fetch));
  let index = 0;
  const fetchContext = {
    urlAccessor,
    responseHandler: options.responseHandler,
    wrapResource: () => {
      const modifier = modifiers[index++];
      typeof modifier === "function" && modifier(fetchContext);
      if (!fetchContext.resource) {
        fetchContext.resource = createResource(
          fetchContext.urlAccessor,
          fetchContext.fetcher,
          options
        );
      }
      return fetchContext.resource;
    }
  };
  fetchContext.wrapResource();
  return fetchContext.resource;
}
var wrapFetcher = (requestContext, wrapper) => {
  const originalFetcher = requestContext.fetcher;
  if (!originalFetcher) {
    throw new Error("could not read resource fetcher");
  }
  requestContext.fetcher = wrapper(originalFetcher) || originalFetcher;
};
var wrapResource = (requestContext, wrapper) => {
  if (!requestContext.resource) {
    throw new Error("could not read resource");
  }
  const [props, actions] = wrapper(requestContext);
  props && Object.defineProperties(requestContext.resource[0], props);
  actions && Object.assign(requestContext.resource[1], actions);
};
var withAbort = () => (requestContext) => {
  wrapFetcher(
    requestContext,
    (originalFetcher) => (requestData, info) => {
      if (requestContext.abortController) {
        requestContext.abortController.abort();
      }
      requestContext.abortController = new AbortController();
      const lastRequestDataObj = (() => {
        for (let l = requestData.length - 1; l >= 0; l--) {
          if (typeof requestData[l] === "object") {
            return requestData[l];
          }
        }
        const obj = {};
        requestData.push(obj);
        return obj;
      })();
      lastRequestDataObj.signal = requestContext.abortController.signal;
      return originalFetcher(requestData, info).catch((err) => {
        if (info.value && err.name === "AbortError") {
          return Promise.resolve(info.value);
        }
        throw err;
      });
    }
  );
  requestContext.wrapResource();
  wrapResource(requestContext, (requestContext2) => [
    {
      aborted: { get: () => requestContext2.abortController?.signal.aborted || false },
      status: { get: () => requestContext2.response?.status },
      response: { get: () => requestContext2.response }
    },
    {
      abort: () => requestContext2.abortController?.abort()
    }
  ]);
};
var withTimeout = (timeout) => (requestContext) => {
  wrapFetcher(
    requestContext,
    (originalFetcher) => (requestData, info) => new Promise((resolve, reject) => {
      setTimeout(() => {
        requestContext.abortController?.abort("timeout");
        reject(new Error("timeout"));
      }, timeout);
      originalFetcher(requestData, info).then(resolve).catch(reject);
    })
  );
  requestContext.wrapResource();
};
var withCatchAll = () => (requestContext) => {
  const [error, setError] = createSignal();
  wrapFetcher(
    requestContext,
    (originalFetcher) => (requestData, info) => originalFetcher(requestData, info).catch((err) => {
      setError(err);
      return Promise.resolve(info.value);
    })
  );
  requestContext.wrapResource();
  const originalResource = requestContext.resource;
  requestContext.resource = [
    Object.defineProperties(() => originalResource?.[0](), {
      ...Object.getOwnPropertyDescriptors(originalResource?.[0]),
      error: { get: () => error() }
    }),
    originalResource?.[1]
  ];
};
var defaultWait = (attempt) => Math.max(1e3 << attempt, 3e4);
var withRetry = (retries, wait = defaultWait, verify = (response) => response?.ok) => (requestContext) => {
  const waitForAttempt = (attempt) => new Promise(
    (resolve) => setTimeout(resolve, typeof wait === "number" ? wait : wait(attempt))
  );
  wrapFetcher(
    requestContext,
    (originalFetcher) => (requestData, info) => {
      const wrappedFetcher = (attempt) => attempt <= retries ? originalFetcher(requestData, info).then(
        (data) => !verify(requestContext.response) ? waitForAttempt(attempt).then(() => wrappedFetcher(attempt + 1)) : data
      ).catch(() => waitForAttempt(attempt).then(() => wrappedFetcher(attempt + 1))) : originalFetcher(requestData, info);
      return wrappedFetcher(0);
    }
  );
  requestContext.wrapResource();
};
var withRefetchEvent = isServer ? () => (requestContext) => {
  requestContext.wrapResource();
} : (options = {}) => (requestContext) => {
  const lastRequestRef = {
    current: void 0
  };
  wrapFetcher(requestContext, (originalFetcher) => (...args) => {
    lastRequestRef.current = [args, void 0];
    return originalFetcher(...args).then((data) => {
      lastRequestRef.current = [args, data];
      return data;
    });
  });
  requestContext.wrapResource();
  const events = options.on || ["visibilitychange"];
  const filter = options.filter || (() => true);
  const handler = (ev) => {
    if (lastRequestRef.current && !(ev.type === "visibilitychange" && document.visibilityState !== "visible") && filter(...lastRequestRef.current, ev)) {
      requestContext.resource?.[1].refetch();
    }
  };
  events.forEach((name) => window.addEventListener(name, handler));
  getOwner() && onCleanup(() => events.forEach((name) => window.removeEventListener(name, handler)));
};
var withAggregation = (dataFilter) => (requestContext) => {
  wrapFetcher(
    requestContext,
    (originalFetcher) => (...args) => originalFetcher(...args).then((data) => {
      const next = dataFilter ? dataFilter(data) : data;
      const current = requestContext.resource?.[0]();
      return Array.isArray(current) ? Array.isArray(next) ? [...current, ...next] : [...current, next] : typeof current === "object" && typeof next === "object" ? { ...current, ...next } : typeof current === "string" && typeof next === "string" ? current + next : current != null ? [current, next] : next;
    })
  );
  requestContext.wrapResource();
};
var defaultCacheOptions = {
  expires: 5e3,
  cache: {}
};
var serializeRequest = (requestData) => JSON.stringify({
  ...typeof requestData[0] === "string" ? { url: requestData[0] } : requestData[0],
  ...requestData[1]
});
var withCache = (options = defaultCacheOptions) => (requestContext) => {
  requestContext.cache = options.cache || requestContext.cache;
  requestContext.expires = options.expires;
  const isExpired = (entry) => typeof options.expires === "number" ? entry.ts + options.expires < (/* @__PURE__ */ new Date()).getTime() : options.expires(entry);
  wrapFetcher(
    requestContext,
    (originalFetcher) => (requestData, info) => {
      const serializedRequest = serializeRequest(requestData);
      const cached = requestContext.cache[serializedRequest];
      const shouldRead = requestContext.readCache?.(cached) !== false;
      if (cached && !isExpired(cached) && shouldRead) {
        return Promise.resolve(cached.data);
      }
      return originalFetcher(requestData, info).then((data) => {
        const cacheEntry = {
          ts: (/* @__PURE__ */ new Date()).getTime(),
          requestData,
          data
        };
        requestContext.cache[serializedRequest] = cacheEntry;
        requestContext.writeCache?.(serializedRequest, cacheEntry);
        return data;
      });
    }
  );
  requestContext.wrapResource();
  const originalRefetch = requestContext.resource[1].refetch;
  const invalidate = (...requestData) => {
    try {
      delete requestContext.cache[serializeRequest(requestData)];
    } catch (e) {
      !isServer && DEV && // eslint-disable-next-line no-console
      console.warn("attempt to invalidate cache for", requestData, "failed with error", e);
    }
  };
  Object.assign(requestContext.resource[1], {
    invalidate,
    refetch: (info) => {
      invalidate();
      return originalRefetch(info);
    }
  });
};
var withRefetchOnExpiry = (pollDelayMs = 100) => (requestContext) => {
  wrapFetcher(
    requestContext,
    (originalFetcher) => (requestData, info) => {
      const serializedRequest = serializeRequest(requestData);
      const cached = requestContext.cache[serializedRequest];
      if (!cached) {
        if (typeof requestContext.expires === "number") {
          setTimeout(() => requestContext.resource?.[1].refetch(), requestContext.expires + 10);
        } else if (typeof requestContext.expires === "function") {
          const delay = pollDelayMs === 0 ? (fn) => requestAnimationFrame(fn) : setTimeout;
          const poll = () => {
            if (requestContext.expires(cached)) {
              requestContext.resource?.[1].refetch();
            } else {
              delay(poll, pollDelayMs);
            }
          };
          poll();
        }
      }
      return originalFetcher(requestData, info);
    }
  );
  requestContext.wrapResource();
};
var withCacheStorage = (storage = localStorage, key = "fetch-cache") => (requestContext) => {
  try {
    const loadedCache = JSON.parse(storage.getItem(key) || "{}");
    Object.assign(requestContext.cache, loadedCache);
  } catch (e) {
    !isServer && DEV && // eslint-disable-next-line no-console
    console.warn("attempt to parse stored request cache failed with error", e);
  }
  const originalWriteCache = requestContext.writeCache;
  requestContext.writeCache = (...args) => {
    originalWriteCache?.(...args);
    try {
      storage.setItem(key, JSON.stringify(requestContext.cache));
    } catch (e) {
      !isServer && DEV && console.warn("attempt to store request cache failed with error", e);
    }
  };
  requestContext.wrapResource();
};

export { createFetch, fetchRequest, serializeRequest, withAbort, withAggregation, withCache, withCacheStorage, withCatchAll, withRefetchEvent, withRefetchOnExpiry, withRetry, withTimeout, wrapFetcher, wrapResource };
